/**
 * Состояние расклада как явная машина состояний (ТЗ 8.4: A ввод → B выложены → C переворот → D трактовка → E дополнение).
 * Вся сетевая логика здесь; экран только рендерит.
 */
import { useCallback, useEffect, useReducer, useRef } from 'react';
import type { Me, ReadingCard, SpreadType } from '@tarot/contracts';
import { ApiError, http, streamSse } from '@/api/client';
import { keys, useInvalidate, useSetMe } from '@/api/hooks';
import { platform } from '@/platform';

export type Phase = 'creating' | 'dealt' | 'streaming' | 'done' | 'append_dealt' | 'append_streaming' | 'error' | 'off_topic' | 'limit';

export interface State {
  phase: Phase;
  readingId: string | null;
  question: string;
  spreadType: SpreadType | null;
  cards: ReadingCard[];
  text: string;
  streamText: string;
  error: string | null;
  limitResetSec: number;
}

type Action =
  | { type: 'created'; readingId: string; spreadType: SpreadType; cards: ReadingCard[] }
  | { type: 'loaded'; readingId: string; question: string; cards: ReadingCard[]; text: string }
  | { type: 'flip'; index: number }
  | { type: 'stream_start' }
  | { type: 'token'; t: string }
  | { type: 'stream_done'; text: string }
  | { type: 'append_dealt'; card: ReadingCard }
  | { type: 'fail'; phase: 'error' | 'off_topic' | 'limit'; error: string; resetSec?: number };

const initial = (question: string): State => ({ phase: 'creating', readingId: null, question, spreadType: null, cards: [], text: '', streamText: '', error: null, limitResetSec: 0 });

function reducer(s: State, a: Action): State {
  switch (a.type) {
    case 'created': return { ...s, phase: 'dealt', readingId: a.readingId, spreadType: a.spreadType, cards: a.cards };
    case 'loaded': return { ...s, phase: 'done', readingId: a.readingId, question: a.question, cards: a.cards, text: a.text };
    case 'flip': return { ...s, cards: s.cards.map((c, i) => (i === a.index ? { ...c, flipped: true } : c)) };
    case 'stream_start': return { ...s, phase: s.cards.length > 3 ? 'append_streaming' : 'streaming', streamText: s.cards.length > 3 ? s.text + '\n\n' : '', error: null };
    case 'token': return { ...s, streamText: s.streamText + a.t };
    case 'stream_done': return { ...s, phase: 'done', text: a.text, streamText: '' };
    case 'append_dealt': return { ...s, phase: 'append_dealt', cards: [...s.cards, a.card] };
    case 'fail': return { ...s, phase: a.phase, error: a.error, limitResetSec: a.resetSec ?? 0 };
  }
}

export function useReadingFlow(opts: { question?: string; readingId?: string }) {
  const [s, dispatch] = useReducer(reducer, opts.question ?? '', initial);
  const setMe = useSetMe();
  const invalidate = useInvalidate();
  const abort = useRef<AbortController | null>(null);
  const started = useRef(false);
  // Актуальное состояние для колбэков, вызываемых из setTimeout (flipAll)
  const latest = useRef(s);
  latest.current = s;

  // Старт: создать расклад либо загрузить из истории
  useEffect(() => {
    if (started.current) return;
    started.current = true;
    (async () => {
      try {
        if (opts.readingId) {
          const r = await http.get<{ id: string; question: string; cards: ReadingCard[]; interpretation: string }>(`/readings/${opts.readingId}`);
          dispatch({ type: 'loaded', readingId: r.id, question: r.question, cards: r.cards.map((c) => ({ ...c, flipped: true })), text: r.interpretation });
          return;
        }
        const r = await http.post<{ readingId: string; spreadType: SpreadType; cards: ReadingCard[]; user: Me }>('/readings', { question: opts.question });
        setMe(r.user);
        dispatch({ type: 'created', readingId: r.readingId, spreadType: r.spreadType, cards: r.cards });
      } catch (e) {
        if (e instanceof ApiError && e.code === 'LIMIT_REACHED') dispatch({ type: 'fail', phase: 'limit', error: e.message, resetSec: (e.details as { resetInSeconds?: number })?.resetInSeconds });
        else if (e instanceof ApiError && e.code === 'OFF_TOPIC') dispatch({ type: 'fail', phase: 'off_topic', error: e.message });
        else dispatch({ type: 'fail', phase: 'error', error: (e as Error).message });
      }
    })();
    return () => abort.current?.abort();
  }, []);

  const stream = useCallback(async (suffix: '/stream' | '/append-stream') => {
    const readingId = latest.current.readingId;
    if (!readingId) return;
    dispatch({ type: 'stream_start' });
    abort.current = new AbortController();
    try {
      const text = await streamSse(`/readings/${readingId}${suffix}`, (t) => dispatch({ type: 'token', t }), abort.current.signal);
      dispatch({ type: 'stream_done', text });
      platform.haptic('success');
      void invalidate(keys.me, keys.readings);
    } catch (e) {
      const err = e as { code: string; message: string };
      if (err.code === 'ABORTED') return;
      dispatch({ type: 'fail', phase: err.code === 'OFF_TOPIC' ? 'off_topic' : 'error', error: err.message });
      void invalidate(keys.me);
    }
  }, [invalidate]);

  const flip = useCallback((index: number) => {
    const cur = latest.current;
    if (cur.cards[index]?.flipped || !['dealt', 'append_dealt'].includes(cur.phase)) return;
    platform.haptic('medium');
    const after = cur.cards.map((c, i) => (i === index ? { ...c, flipped: true } : c));
    latest.current = { ...cur, cards: after };
    dispatch({ type: 'flip', index });
    void http.post(`/readings/${cur.readingId}/flip`, { index }).catch(() => undefined);
    if (after.every((c) => c.flipped)) void stream(after.length > 3 ? '/append-stream' : '/stream');
  }, [stream]);

  const flipAll = useCallback(() => {
    latest.current.cards.forEach((c, i) => { if (!c.flipped) setTimeout(() => flip(i), i * 350); });
  }, [flip]);

  /** Дополнить: бросает ApiError INSUFFICIENT_FUNDS — экран показывает пейвол. */
  const append = useCallback(async () => {
    const r = await http.post<{ card: ReadingCard; index: number; user: Me }>(`/readings/${s.readingId}/append`);
    setMe(r.user);
    platform.haptic('medium');
    dispatch({ type: 'append_dealt', card: r.card });
  }, [s.readingId, setMe]);

  const retry = useCallback(() => void stream(s.cards.length > 3 ? '/append-stream' : '/stream'), [stream, s.cards.length]);

  const rate = useCallback((rating: 1 | -1) => void http.post(`/readings/${s.readingId}/rate`, { rating }), [s.readingId]);

  return { state: s, flip, flipAll, append, retry, rate };
}
